import { mdiLoading } from '@mdi/js';
import Icon from '@mdi/react';
import iziToast from 'izitoast';
import { SearchIcon } from 'lucide-react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslate } from 'sbx-crm-menu-ui';
import { AppAgentContext } from 'src/context/ContextProvider';
import useIgnoreRenderEffect from 'src/hooks/useIgnoreRenderEffect';
import { Assets, getFolderFilesService } from 'src/services/AssetService';
import { appActions, DataStates, State } from 'src/store';
import { AiAgent } from 'src/types/AiAgent';
import { deleteFileService } from '../../../services/AssetService';
import { ModalType } from '../../../types/Modal';
import { DragAndDropImages } from '../DragAndDropImages';
import ModalComponent from './index';
import { shortenFilename } from 'src/utils';

export interface AssetsAgentProps {
    title: string;
    onClose: () => void;
    data?: AiAgent;
    setData: (item: string) => void;
}

export type AssetsValidate = Assets & { isUrl?: boolean };

export type AssetsAgentPropsWrapper = { type: ModalType.ASSETS_AGENT; data: AssetsAgentProps };

export const AssetsAgentModal = ({ title, onClose, setData }: AssetsAgentProps) => {
    const {
        state: {
            session: {
                ai_agent_config: { path_avatars = '', folder_key_avatars = '' },
            },
        },
        dispatch,
    } = useContext(AppAgentContext);
    const [files, setFiles] = useState<AssetsValidate[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [uploadImages, setUploadImages] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslate('common');

    const startingData = useCallback(async () => {
        setIsLoading(true);
        const res = await getFolderFilesService(folder_key_avatars);
        if (res.success) {
            setFiles(res.items);
            setIsLoading(false);
        }
        setIsLoading(false);
    }, []);

    useIgnoreRenderEffect(async () => {
        await startingData();
    }, [startingData]);

    const assetsFilter = useMemo(() => {
        if (searchValue.length > 2) {
            return files.filter((file) => file.name.toLowerCase().includes(searchValue.toLowerCase()));
        }
        return files;
    }, [searchValue, files]);

    const deleteAvatar = (key: string) => {
        dispatch(
            appActions.openModal({
                type: ModalType.CONFIRMATION,
                data: {
                    title: t('delete_image'),
                    description: t('confirm_delete_image'),
                    onClose: () => dispatch(appActions.closeModal(ModalType.CONFIRMATION)),
                    state: DataStates.AGENT_ASSET,
                    onConfirm: async () => {
                        dispatch(appActions.onLoading({ type: DataStates.AGENT_ASSET, status: State.LOADING }));
                        const res = await deleteFileService(key);
                        if (res.success) {
                            const filesFilter = files.filter((file) => file.key !== key);
                            setFiles(filesFilter);
                            iziToast.success({
                                message: t('image_deleted'),
                                position: 'topCenter',
                            });
                            dispatch(appActions.onLoading({ type: DataStates.AGENT_ASSET, status: State.SUCCESS }));
                            dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                        } else {
                            dispatch(appActions.onLoading({ type: DataStates.AGENT_ASSET, status: State.ERROR }));
                            dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                        }
                    },
                },
            })
        );
    };

    return (
        <ModalComponent onClose={onClose} className="bg-white max-w-[550px]" title={title} open>
            {() => {
                return (
                    <div className="mr-2">
                        <div className="flex justify-end mb-2">
                            <button className="btn btn-sm" onClick={() => setUploadImages(!uploadImages)}>
                                {uploadImages ? t('cancel') : t('upload_images')}
                            </button>
                        </div>
                        {uploadImages ? (
                            <DragAndDropImages files={files} setFiles={setFiles} setUploadImages={setUploadImages} />
                        ) : (
                            <>
                                <div className="relative w-full">
                                    <input
                                        type="text"
                                        className="px-4 py-1 h-[38px] border-2 w-full border-solid border-gray-200 rounded outline-none"
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        value={searchValue}
                                        placeholder={t('search') + '...'}
                                    />
                                    <SearchIcon className="w-4 h-4 text-gray-400 absolute top-1/2 -translate-y-1/2 right-2" />
                                </div>
                                <div className="h-[400px] overflow-auto">
                                    {assetsFilter.length === 0 && !isLoading && (
                                        <p className="text-gray-400 text-center mt-3">{t('no_results')}</p>
                                    )}
                                    <div className="mt-2 grid md:grid-cols-3">
                                        {assetsFilter.map((file, index) => (
                                            <div key={index} className="w-[150px] h-[150px] relative">
                                                <img
                                                    className="w-full h-full object-cover"
                                                    src={file.isUrl ? file.url : `${path_avatars}/${file.name}`}
                                                    alt={file.name}
                                                    onError={(e) => console.log(e)}
                                                />
                                                <div className="px-2 flex top-0 left-0 absolute w-full h-full opacity-0 hover:opacity-100 hover:bg-[rgba(0,0,0,0.6)]">
                                                    <div className="absolute bottom-2 flex flex-col gap-1 items-center">
                                                        <p className="break-all whitespace-normal text-white">
                                                            {shortenFilename(file.name, 20)}
                                                        </p>
                                                        <div className="flex gap-1">
                                                            <button
                                                                className="btn btn-sm !bg-blue-500"
                                                                onClick={() => {
                                                                    if (file.isUrl) {
                                                                        setData(file.url);
                                                                    } else {
                                                                        setData(`${path_avatars}/${file.name}`);
                                                                    }
                                                                    onClose();
                                                                }}
                                                            >
                                                                {t('select')}
                                                            </button>
                                                            <button
                                                                className="btn btn-sm-danger"
                                                                onClick={() => deleteAvatar(file.key)}
                                                            >
                                                                {t('delete')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {isLoading && (
                                        <div className="flex justify-center items-center w-full h-60">
                                            <Icon className="mr-1 text-primary" size={2} path={mdiLoading} spin />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                );
            }}
        </ModalComponent>
    );
};
